import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import useAdminPageMeta from '../../hooks/useAdminPageMeta';
import { Users, DollarSign, MessageCircle, ShoppingBag, UserPlus, Activity } from 'lucide-react';
import { getIcon } from '../../lib/icons';

function StatCard({ icon: Icon, label, value, accent = '#c4a144' }) {
  return (
    <div className="ee-card p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-ee-muted">{label}</div>
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${accent}1A`, border: `1px solid ${accent}55` }}><Icon size={16} style={{ color: accent }} /></div>
      </div>
      <div className="text-3xl font-serif text-white mt-3">{value}</div>
    </div>
  );
}

export default function AdminDashboardPage() {
  useAdminPageMeta('Dashboard');
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/api/admin/stats'), api.get('/api/admin/activity?limit=15')])
      .then(([s, a]) => { setStats(s.data); setActivity(a.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4 sm:p-6 md:p-10">
      <div className="mb-6 md:mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-gold/80 mb-2">Overview</div>
        <h1 className="hero-title text-3xl md:text-4xl text-white">Control Center</h1>
        <p className="text-ee-text2 mt-2">Pulse of your platform.</p>
      </div>
      {loading ? (
        <div className="text-ee-muted">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Users" value={stats?.total_users ?? 0} />
            <StatCard icon={ShoppingBag} label="With Access" value={stats?.users_with_access ?? 0} accent="#4CAF50" />
            <StatCard icon={DollarSign} label="Revenue" value={`$${stats?.total_revenue ?? 0}`} accent="#2196F3" />
            <StatCard icon={MessageCircle} label="EE Messages" value={stats?.total_messages ?? 0} accent="#E91E63" />
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 ee-card p-6">
              <div className="flex items-center gap-2 mb-4"><Activity size={16} className="text-gold" /><h2 className="text-white text-lg font-serif">Recent Activity</h2></div>
              <div className="divide-y divide-ee-border">
                {activity.length === 0 && <div className="text-ee-muted text-sm py-4">No activity yet.</div>}
                {activity.map((a, i) => {
                  const Icon = getIcon(a.icon);
                  return (
                    <div key={i} className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0"><Icon size={14} className="text-gold" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{a.title}</div>
                          <div className="text-xs text-ee-text2 truncate">{a.description}</div>
                        </div>
                      </div>
                      <div className="text-[11px] text-ee-dim whitespace-nowrap sm:flex-shrink-0 pl-11 sm:pl-0">{new Date(a.timestamp).toLocaleString()}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="ee-card p-6">
              <h2 className="text-white text-lg font-serif mb-4">Quick stats</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-ee-muted">Total purchases</span><span className="text-white">{stats?.total_purchases ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-ee-muted">Recent signups (7d)</span><span className="text-white">{stats?.recent_signups ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-ee-muted">Recent purchases (7d)</span><span className="text-white">{stats?.recent_purchases ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-ee-muted">EE conversations</span><span className="text-white">{stats?.ee_conversations ?? 0}</span></div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
